/**
 * useDimensionCauseRecords - Data fetching for dimension + cause specific records
 */

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { FilterConfig } from '../../../records/filter';
import type {
  CauseReportDimensionCauseRecords,
  ConductDimensionId,
  UseDimensionCauseRecordsReturn,
} from '../types';
import {
  dimensionsKeys,
  CAUSE_REPORT_STALE_TIME,
  DIALOG_RECORDS_PAGE_SIZE,
} from '../constants';
import { fetchWithAuth } from '../../../../../utils/api';
import { mapFiltersToApiParams, transformRecord } from '../utils';
import type { ApiRecord } from '../utils';

interface DimensionCauseRecordsApiResponse {
  records: ApiRecord[];
  total_records: number;
  dimension_id: string;
  dimension_name: string;
  cause_id: string;
  cause_name: string;
  page: number;
  page_size: number;
  total_pages: number;
}

function transformDimensionCauseRecords(
  data: DimensionCauseRecordsApiResponse
): CauseReportDimensionCauseRecords {
  return {
    records: data.records.map(transformRecord),
    totalRecords: data.total_records,
    dimensionId: data.dimension_id,
    dimensionName: data.dimension_name,
    causeId: data.cause_id,
    causeName: data.cause_name,
    page: data.page,
    pageSize: data.page_size,
    totalPages: data.total_pages,
  };
}

async function fetchDimensionCauseRecords(
  datasetId: string,
  dimensionId: string,
  causeId: string,
  params: Record<string, string>,
  page: number
): Promise<DimensionCauseRecordsApiResponse> {
  const queryParams = { ...params };
  queryParams.page = String(page);
  queryParams.page_size = String(DIALOG_RECORDS_PAGE_SIZE);

  const queryString = new URLSearchParams(queryParams).toString();

  return fetchWithAuth(
    `/v1/datasets/${datasetId}/cause-report/dimensions/${dimensionId}/causes/${causeId}/records?${queryString}`
  );
}

export function useDimensionCauseRecords(
  datasetId: string,
  dimensionId: ConductDimensionId | null,
  causeId: string | null,
  filters: FilterConfig,
  page: number = 1,
  enabled: boolean = true
): UseDimensionCauseRecordsReturn {
  const filterParams = useMemo(() => mapFiltersToApiParams(filters), [filters]);

  const query = useQuery({
    queryKey: [
      ...dimensionsKeys.all,
      'cause-records',
      datasetId,
      dimensionId,
      causeId,
      filterParams,
      page,
    ],
    queryFn: () =>
      fetchDimensionCauseRecords(datasetId, dimensionId!, causeId!, filterParams, page),
    select: transformDimensionCauseRecords,
    staleTime: CAUSE_REPORT_STALE_TIME,
    enabled: !!datasetId && !!dimensionId && !!causeId && enabled,
  });

  return {
    data: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  };
}
